import { useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { useSignatureRequests } from '../../hooks/useSignatures'
import { SECTION_ICONS } from '../../lib/section-icons'
import { PageHeader } from '../shared/PageHeader'
import { Button } from '../ui/button'
import { EmptyState } from '../ui/empty-state'
import { SignatureRequestCard } from './SignatureRequestsPanel'

const FILTERS = [
    { key: 'open', status: 'draft,pending' },
    { key: 'done', status: 'completed' },
    { key: 'closed', status: 'declined,expired,cancelled' },
    { key: 'all', status: undefined },
] as const

/**
 * Every signature request of the organization (spec 2026-10-05-05 §5),
 * filtered by status through the URL. A request is created from the PDF it
 * signs, on the document page.
 */
export function SignatureRequestsPage() {
    const { org } = useParams({ from: '/$org/signatures/' })
    const search = useSearch({ from: '/$org/signatures/' })
    const navigate = useNavigate()
    const { t } = useTranslation('signatures')
    useBreadcrumbs([{ title: t('common:nav.home'), path: `/${org}` }, { title: t('title') }])
    const status = search.status ?? 'draft,pending'
    const { data, isLoading } = useSignatureRequests(org, { status: status === 'all' ? undefined : status })
    const requests = data?.data ?? []

    return (
        <div className="space-y-6" data-testid="signatures-page">
            <PageHeader icon={SECTION_ICONS.signatures} title={t('title')} description={t('description')} />
            <div className="flex flex-wrap gap-2" role="tablist">
                {FILTERS.map((f) => {
                    const value = f.status ?? 'all'
                    const active = value === status
                    return (
                        <Button
                            key={f.key}
                            role="tab"
                            aria-selected={active}
                            variant={active ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => navigate({ to: '/$org/signatures', params: { org }, search: { status: value } })}
                            data-testid={`signatures-filter-${f.key}`}
                        >
                            {t(`filters.${f.key}`)}
                        </Button>
                    )
                })}
            </div>
            {isLoading ? (
                <p className="text-sm text-muted-foreground">{t('loading')}</p>
            ) : requests.length === 0 ? (
                <EmptyState icon={SECTION_ICONS.signatures} title={t('empty_title')} description={t('empty_description')} data-testid="signatures-empty" />
            ) : (
                <ul className="space-y-6 rounded-lg border p-4" data-testid="signatures-list">
                    {requests.map((r) => (
                        <SignatureRequestCard key={r.uid} org={org} request={r} />
                    ))}
                </ul>
            )}
        </div>
    )
}
